import { useEffect, useState } from "react";
import { DateValidator2 } from "../../utils/validation";
import SmallModal from "../../components/modal/SmallModal";
import Dropdown from "../../components/inputs/Dropdown";
import Input from "../../components/inputs/Input";
import NPInput from "../../components/inputs/NPInput";
import { HelpButton } from "../../components/btn/Buttons";
import moment from "moment";
import Button from "../../components/btn/Button";
import { fy_status_update, is_ftl_api, profile_update_api, distrct_data_api, pincode_verify_api } from "../../utils/apis";
import { toast } from "react-toastify";
import { get_home_data, get_pin_data } from "../profile/UsersSlice";
import { useSelector } from "react-redux";
import store from "../../redux/store";
import Loader from "../../components/Loader";
import { get_complete_list } from "../../redux/slice/completeListSlice";
import {
  get_eff_date,
  get_effective_date,
  get_omc,
  get_user_id,
} from "../../redux/slice/SlideSlice";
import { Alerts } from "../../components/Alerts/Alerts";
import BusinessDashboard from "./BusinessDashboard";

export const Home = () => {
  const getStoredUser = () => {
    try {
      return JSON.parse(localStorage.getItem("userId") || "{}");
    } catch (error) {
      return {};
    }
  };

  const getWizardFlagsFromStorage = () => {
    const storedUser = getStoredUser();
    const IS_FTL_VALUE = localStorage.getItem("IS_FTL");
    const FY_STATUS_VALUE = localStorage.getItem("FY_STATUS");

    const IS_FTL =
      IS_FTL_VALUE !== null ? Number(IS_FTL_VALUE) : Number(storedUser?.IS_FTL);
    const FY_STATUS =
      FY_STATUS_VALUE !== null
        ? FY_STATUS_VALUE === "true"
        : Boolean(storedUser?.FY_STATUS);

    return { IS_FTL, FY_STATUS };
  };

  const shouldShowAnnualWizard = () => {
    const currentFY = Number(getCurrentFinancialYear());
    const storedUser = getStoredUser();
    const apiFY = Number(storedUser?.FY);

    if (!Number.isFinite(currentFY) || !Number.isFinite(apiFY)) {
      return false;
    }

    // Backend FY indicates latest completed serial setup FY.
    // Show annual wizard only when backend FY is behind current FY.
    return apiFY < currentFY;
  };

  const markAnnualWizardCompleted = () => {
    const currentFY = Number(getCurrentFinancialYear());
    if (!Number.isFinite(currentFY)) {
      return;
    }

    const IS_FTL_VALUE = localStorage.getItem("IS_FTL");
    const parsedISFTL =
      IS_FTL_VALUE !== null ? Number(IS_FTL_VALUE) : undefined;
    const storedUser = getStoredUser();
    localStorage.setItem(
      "userId",
      JSON.stringify({
        ...storedUser,
        FY: currentFY,
        FY_STATUS: false,
        ...(Number.isFinite(parsedISFTL) ? { IS_FTL: parsedISFTL } : {}),
      }),
    );
    localStorage.setItem("FY_STATUS", "false");
  };
  // ── Wizard state management ──────────────────────────────────────────────
  const [showWizard, setShowWizard] = useState(() => {
    const { IS_FTL, FY_STATUS } = getWizardFlagsFromStorage();

    return IS_FTL === 0 || FY_STATUS || shouldShowAnnualWizard();
  });

  // Persist step in localStorage to survive page refreshes
  const [step, setStep] = useState(() => {
    const savedStep = localStorage.getItem("wizard_step");
    const initialStep = savedStep ? parseInt(savedStep) : 1;
    console.log("🎬 Initializing step from localStorage:", initialStep);
    return initialStep;
  });

  const [isFTLMode] = useState(() => getWizardFlagsFromStorage().IS_FTL === 0);

  // Update localStorage whenever step changes
  const updateStep = (newStep) => {
    localStorage.setItem("wizard_step", newStep);
    setStep(newStep);
  };

  const resetWizardStep = () => {
    localStorage.removeItem("wizard_step");
    setStep(1);
  };

  // ── Form data states ─────────────────────────────────────────────────────
  const [isFTLData, setIsFTLData] = useState({
    FY: getCurrentFinancialYear(),
    effe_date: "",
    b2b: "0001",
    b2c: "0001",
    payment_voucher: "0001",
    reciept_voucher: "0001",
    delivery_challan: "0001",
    quotation: "0001",
    purchase_order: "0001",
    expenses_voucher: "0001",
    cash_balance: "0.00",
    load_balance: "0.00",
    svtv_balance: "0.00",
  });

  const [addData, setAddData] = useState({
    unit_name: "",
    address1: "",
    address2: "",
    state: "",
    district: "",
    pincode: "",
    location: "",
  });

  // Optional second godown address
  const [addData2, setAddData2] = useState({
    unit_name: "",
    address1: "",
    address2: "",
    state: "",
    district: "",
    pincode: "",
    location: "",
  });

  const [showSecondGodown, setShowSecondGodown] = useState(false);

  const [pinData, setPinData] = useState({
    set_pin: "",
    confirm_pin: "",
  });

  // ── UI states ────────────────────────────────────────────────────────────
  const [districtList2, setDistrictList2] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [districtList3, setDistrictList3] = useState([]); // For second godown
  const [locationList2, setLocationList2] = useState([]); // For second godown
  const [open1, setOpen1] = useState(false);
  const [mess, setMess] = useState("");
  const [showLoader, setShowLoader] = useState(false);

  // ── Redux selectors ──────────────────────────────────────────────────────
  const { stateList } = useSelector((state) => state.completeList);
  const { today, user_id } = useSelector((state) => state.other);

  // ── Initialize app data ──────────────────────────────────────────────────
  useEffect(() => {
    const storedUser = getStoredUser();
    // Initialize flags from login payload only if keys are missing.
    // Do not overwrite runtime-updated values on refresh.
    if (
      localStorage.getItem("IS_FTL") === null &&
      storedUser?.IS_FTL !== undefined
    ) {
      localStorage.setItem("IS_FTL", String(storedUser?.IS_FTL));
    }
    if (
      localStorage.getItem("FY_STATUS") === null &&
      storedUser?.FY_STATUS !== undefined
    ) {
      localStorage.setItem("FY_STATUS", String(storedUser?.FY_STATUS));
    }
  }, []);

  useEffect(() => {
    store.dispatch(get_effective_date());
    store.dispatch(get_user_id());
    store.dispatch(get_omc());
    store.dispatch(get_complete_list());
  }, []);

  useEffect(() => {
    if (user_id && !showWizard) {
      store.dispatch(get_home_data(user_id));
    }
  }, [user_id, showWizard]);


  // ── Helper functions ─────────────────────────────────────────────────────
  function getCurrentFinancialYear() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const fiscalStartMonth = 3;
    const fiscalYear =
      today.getMonth() < fiscalStartMonth ? currentYear - 1 : currentYear;
    return `${fiscalYear.toString().slice(2)}`;
  }

  const getFinancialYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const startYear = currentMonth < 3 ? currentYear - 1 : currentYear;
    const years = [];
    for (let i = startYear; i > startYear - 3; i--) {
      years.push(`${i}-${(i + 1).toString().substring(2, 4)}`);
    }
    return years;
  };

  // ── Step 1: Validation and submission ────────────────────────────────────
  const validation = () => {
    const arr = [];
    arr.push(!isFTLData?.b2b || isFTLData?.b2b.length == 0 ? "Please Enter B2B INVOICE COUNTER" : "");
    arr.push(!isFTLData?.b2c || isFTLData?.b2c.length == 0 ? "Please Enter B2C INVOICE COUNTER" : "");
    arr.push(!isFTLData?.payment_voucher || isFTLData?.payment_voucher.length == 0 ? "Please Enter PAYMENT VOUCHER" : "");
    arr.push(!isFTLData?.reciept_voucher || isFTLData?.reciept_voucher.length == 0 ? "Please Enter RECIEPTS VOUCHER" : "");
    arr.push(!isFTLData?.delivery_challan || isFTLData?.delivery_challan.length == 0 ? "Please Enter DELIVERY CHALLAN" : "");
    arr.push(!isFTLData?.quotation || isFTLData?.quotation.length == 0 ? "Please Enter QUOTATION" : "");
    arr.push(!isFTLData?.purchase_order || isFTLData?.purchase_order.length == 0 ? "Please Enter PURCHASE ORDER" : "");
    arr.push(!isFTLData?.expenses_voucher || isFTLData?.expenses_voucher.length == 0 ? "Please Enter EXPENSES VOUCHER" : "");
    return arr;
  };

  const HandleSubmitFTL = async () => {
    const arr = [];
    arr.push(!isFTLData.FY ? "Please Select FY" : "");

    const currentDate = moment();
    const currentFYStart = currentDate.month() < 3
      ? moment().subtract(1, "years").startOf("year").add(3, "months")
      : moment().startOf("year").add(3, "months");
    const formattedFYStart = currentFYStart.format("DD/MM/YYYY");

    const data_valid = DateValidator2(
      moment(isFTLData?.effe_date).format("DD/MM/YYYY"),
      today,
      formattedFYStart,
      `Effective Date cant be before ${moment("01/04/2020").format("DD/MM/YYYY")}`,
      `Effective Date cant be a future date`,
    );

    arr.push(
      !isFTLData?.effe_date || isFTLData?.effe_date?.length != 10
        ? "Please Select Effective Date"
        : data_valid != true ? data_valid?.[0] : "",
    );

    const validate = validation();
    arr.push(...validate);

    arr.push(!isFTLData?.cash_balance || isFTLData?.cash_balance.length == 0 ? "Please Enter CASH BALANCE" : isFTLData?.cash_balance <= 0 ? "CASH BALANCE must be greater than zero" : "");
    arr.push(!isFTLData?.load_balance || isFTLData?.load_balance.length == 0 ? "Please Enter LOAD ACCOUNT OPEN BALANCE" : isFTLData?.load_balance <= 0 ? "LOAD BALANCE must be greater than zero" : "");
    arr.push(!isFTLData?.svtv_balance || isFTLData?.svtv_balance.length == 0 ? "Please Enter SVTV ACCOUNT BALANCE" : isFTLData?.svtv_balance <= 0 ? "SVTV BALANCE must be greater than zero" : "");

    if (arr.filter((v) => v !== "")?.length > 0) {
      setOpen1(true);
      setMess(<Alerts data={arr} />);
    } else {
      try {
        setShowLoader(true);
        const body = new FormData();
        body.append("action", "ftl");
        body.append("agencyid", user_id);
        body.append("fin_year", isFTLData?.FY);
        body.append("eff_date", isFTLData?.effe_date);
        body.append("sino", isFTLData?.b2b);
        body.append("csno", isFTLData?.b2c);
        body.append("payno", isFTLData?.payment_voucher);
        body.append("recno", isFTLData?.reciept_voucher);
        body.append("delno", isFTLData?.delivery_challan);
        body.append("quono", isFTLData?.quotation);
        body.append("pono", isFTLData?.purchase_order);
        body.append("expno", isFTLData?.expenses_voucher);
        body.append("cash", isFTLData?.cash_balance);
        body.append("load", isFTLData?.load_balance);
        body.append("svtv", isFTLData?.svtv_balance);

        const responce = await is_ftl_api(body);
        setShowLoader(false);

        console.log("FTL API Response:", responce);

        if (responce.success) {
          // Update step FIRST before any other operations
          console.log("Before updateStep(2), current step:", step);
          updateStep(2);
          console.log("After updateStep(2) called, localStorage:", localStorage.getItem("wizard_step"));

          // Then do other operations
          toast(responce.message);
          const effectiveDate = responce?.effective_date || responce?.date;
          if (effectiveDate) {
            store.dispatch(get_eff_date(effectiveDate));
            localStorage.setItem("eff_date", effectiveDate);
          }
        } else {
          setOpen1(true);
          setMess(responce.message);
        }
      } catch (error) {
        console.log(error);
        setShowLoader(false);
      }
    }
  };

  const handleSubmitData = async () => {
    const arr = validation();

    if (arr.filter((v) => v !== "")?.length > 0) {
      setOpen1(true);
      setMess(<Alerts data={arr} />);
    } else {
      try {
        setShowLoader(true);
        const body = new FormData();
        body.append("action", "create");
        body.append("agencyid", user_id);
        body.append("sino", isFTLData?.b2b);
        body.append("csno", isFTLData?.b2c);
        body.append("payno", isFTLData?.payment_voucher);
        body.append("recno", isFTLData?.reciept_voucher);
        body.append("delno", isFTLData?.delivery_challan);
        body.append("quono", isFTLData?.quotation);
        body.append("pono", isFTLData?.purchase_order);
        body.append("expno", isFTLData?.expenses_voucher);

        const responce = await fy_status_update(body);
        setShowLoader(false);

        if (responce.success) {
          toast(responce.message);
          // For new FY, directly close wizard after step 1
          localStorage.setItem("FY_STATUS", "false");
          markAnnualWizardCompleted();
          resetWizardStep();
          store.dispatch(get_home_data(user_id));
          setShowWizard(false);
        } else {
          setOpen1(true);
          setMess(responce.message);
        }
      } catch (error) {
        console.log(error);
        setShowLoader(false);
      }
    }
  };

  // ── Step 2: Godown address handlers ──────────────────────────────────────
  const handleDistrict3 = async (data) => {
    try {
      const body = new FormData();
      body.append("stateid", data || addData?.state);
      const response = await distrct_data_api(body);
      if (response.success) {
        setDistrictList2(response?.data?.reponselist?.districts);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDistrict4 = async (data) => {
    try {
      const body = new FormData();
      body.append("stateid", data || addData2?.state);
      const response = await distrct_data_api(body);
      if (response.success) {
        setDistrictList3(response?.data?.reponselist?.districts);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const get_location = async (data, stateData) => {
    if ((stateData || stateData?.length > 0) && (data || data?.length === 6)) {
      try {
        const body = new FormData();
        body?.append("pincode", data);
        body?.append("state", stateData);
        const responce = await pincode_verify_api(body);
        if (responce.success) {
          setLocationList(responce?.locations);
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const get_location2 = async (data, stateData) => {
    if ((stateData || stateData?.length > 0) && (data || data?.length === 6)) {
      try {
        const body = new FormData();
        body?.append("pincode", data);
        body?.append("state", stateData);
        const responce = await pincode_verify_api(body);
        if (responce.success) {
          setLocationList2(responce?.locations);
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const handleAddAddress = async () => {
    const arr = [];

    // Validate first godown (required)
    if (!addData?.unit_name || addData?.unit_name?.length === 0) {
      arr.push("Please Enter GODOWN NAME");
    }
    if (!addData?.address1 || addData?.address1?.trim()?.length <= 5) {
      arr.push(!addData?.address1 ? "Please Enter ADDRESS LINE 1" : "ADDRESS LINE 1 is too short");
    }
    if (!addData?.state) {
      arr.push("Please Select STATE");
    }
    if (!addData?.district) {
      arr.push("Please Select DISTRICT");
    }
    if (!addData?.pincode || addData?.pincode?.length !== 6) {
      arr.push(!addData?.pincode ? "Please Enter PINCODE" : "PINCODE must be 6 digits");
    }
    if (!addData?.location) {
      arr.push("Please Select LOCATION");
    }

    // Validate second godown (optional, but if started, must be complete)
    if (showSecondGodown) {
      if (!addData2?.unit_name || addData2?.unit_name?.length === 0) {
        arr.push("Please Enter GODOWN 2 NAME");
      }
      if (!addData2?.address1 || addData2?.address1?.trim()?.length <= 5) {
        arr.push(!addData2?.address1 ? "Please Enter GODOWN 2 ADDRESS LINE 1" : "GODOWN 2 ADDRESS LINE 1 is too short");
      }
      if (!addData2?.state) {
        arr.push("Please Select GODOWN 2 STATE");
      }
      if (!addData2?.district) {
        arr.push("Please Select GODOWN 2 DISTRICT ");
      }
      if (!addData2?.pincode || addData2?.pincode?.length !== 6) {
        arr.push(!addData2?.pincode ? "Please Enter GODOWN 2 PINCODE" : "GODOWN 2 PINCODE must be 6 digits");
      }
      if (!addData2?.location) {
        arr.push("Please Select GODOWN 2 LOCATION");
      }
    }

    if (arr.filter((v) => v !== "")?.length > 0) {
      setOpen1(true);
      setMess(<Alerts data={arr} />);
    } else {
      try {
        setShowLoader(true);

        // Add first godown
        const body = new FormData();
        body.append("action", "addAddress");
        body.append("agencyid", user_id);
        body.append("addr_type", "2");
        body.append("unit_name", addData?.unit_name?.trim());
        body.append("cnt_name", "");
        body.append("cnt_designation", "");
        body.append("cnt_mobile", "");
        body.append("address1", addData?.address1 || "");
        body.append("address2", addData?.address2 || "");
        body.append("district", addData?.district);
        body.append("state", addData?.state);
        body.append("pincode", addData?.pincode);
        body.append("location", addData?.location);

        const responce = await profile_update_api(body);

        // If first godown added successfully and second godown is filled, add it too
        if (responce.success && showSecondGodown) {
          const body2 = new FormData();
          body2.append("action", "addAddress");
          body2.append("agencyid", user_id);
          body2.append("addr_type", "2");
          body2.append("unit_name", addData2?.unit_name?.trim());
          body2.append("cnt_name", "");
          body2.append("cnt_designation", "");
          body2.append("cnt_mobile", "");
          body2.append("address1", addData2?.address1 || "");
          body2.append("address2", addData2?.address2 || "");
          body2.append("district", addData2?.district);
          body2.append("state", addData2?.state);
          body2.append("pincode", addData2?.pincode);
          body2.append("location", addData2?.location);

          const responce2 = await profile_update_api(body2);
          setShowLoader(false);

          if (responce2.success) {
            toast.success("Both godown addresses added successfully");
            updateStep(3);
          } else {
            setOpen1(true);
            setMess("First godown added, but second godown failed: " + responce2.message);
          }
        } else {
          setShowLoader(false);
          if (responce.success) {
            toast(responce.message);
            updateStep(3);
          } else {
            setOpen1(true);
            setMess(responce.message);
          }
        }
      } catch (error) {
        console.log(error);
        setShowLoader(false);
      }
    }
  };

  // ── Step 3: Set PIN handler ──────────────────────────────────────────────
  const handle_set_pin = async () => {
    const error = [];
    if (!pinData?.set_pin) {
      error.push("Please enter a PIN.");
    } else if (!/^\d{4}$/.test(pinData.set_pin)) {
      error.push("PIN must be exactly 4 digits.");
    }
    if (!pinData?.confirm_pin) {
      error.push("Please confirm your PIN.");
    } else if (pinData.set_pin !== pinData.confirm_pin) {
      error.push("PIN and Confirm PIN do not match.");
    }

    if (error?.length === 0) {
      try {
        setShowLoader(true);
        const body = new FormData();
        body.append("action", "setpin");
        body.append("agencyid", user_id);
        body.append("pinnumber", pinData?.set_pin);

        const responce = await profile_update_api(body);
        setShowLoader(false);

        if (responce.success) {
          store.dispatch(get_pin_data(1));
          toast.success(responce.message);
          localStorage.setItem("IS_FTL", "1");
          localStorage.setItem("FY_STATUS", "false");
          markAnnualWizardCompleted();
          resetWizardStep();
          store.dispatch(get_home_data(user_id));
          setShowWizard(false);
        } else {
          toast.error(responce.message);
        }
      } catch (error) {
        console.log(error);
        setShowLoader(false);
      }
    } else {
      setOpen1(true);
      setMess(<Alerts data={error} />);
    }
  };

  return (
    <div>
      <BusinessDashboard />

      {showWizard && (
        <SmallModal
          closeBtnHide={false}
          okBtnHide={false}
          isOpen={showWizard}
          setIsOpen={() => { }}
          style={{ minWidth: "40rem" }}
          okbtn={() => { }}
          header_right={
            <HelpButton
              link={[
                "https://youtu.be/A971FsmllM4",
                "https://youtu.be/v5AAVwSlxqs",
              ]}
            />
          }
          title={
            step === 1
              ? "PLEASE ENTER THE FOLLOWING DETAILS"
              : step === 2
                ? "ADD GODOWN ADDRESS"
                : "Suppport and security pin "
          }
          mess={
            <div className="pin-ver" style={{ margin: "2rem 0" }}>
              {step === 1 && (
                <>
                  {isFTLMode && (
                    <div
                      className="pin-ver-1st"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                        FINANCIAL YEAR
                      </div>
                      <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                        <Dropdown
                          fontSize={"15px"}
                          value={isFTLData.FY}
                          onChange={(e) => {
                            setIsFTLData((prevData) => ({
                              ...prevData,
                              FY: e.target.value,
                            }));
                          }}
                          options={
                            <>
                              <option value={""}>SELECT</option>
                              {getFinancialYearOptions().map((year, index) => (
                                <option key={index} value={year?.slice(2, 4)}>
                                  {year}
                                </option>
                              ))}
                            </>
                          }
                        />
                      </div>
                      <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                        EFFECTIVE DATE
                      </div>
                      <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                        <Input
                          type={"date"}
                          value={isFTLData?.effe_date}
                          onChange={(e) => {
                            if (e.target.value.length <= 10) {
                              setIsFTLData({
                                ...isFTLData,
                                effe_date: e.target.value,
                              });
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div
                    className="pin-ver-1st"
                    style={{
                      display: "flex",
                      margin: "2rem 0",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      B2B INVOICE COUNTER
                    </div>
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      <NPInput
                        type={"text"}
                        prefixItem={`SI-${isFTLData.FY}-`}
                        onChange={(e) => {
                          setIsFTLData((prevData) => ({
                            ...prevData,
                            b2b: e.target.value,
                          }));
                        }}
                        value={isFTLData.b2b}
                      />
                    </div>
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      B2C INVOICE COUNTER
                    </div>
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      <NPInput
                        prefixItem={`CS-${isFTLData.FY}-`}
                        onChange={(e) => {
                          setIsFTLData((prevData) => ({
                            ...prevData,
                            b2c: e.target.value,
                          }));
                        }}
                        value={isFTLData.b2c}
                        type={"text"}
                      />
                    </div>
                  </div>

                  <div
                    className="pin-ver-1st"
                    style={{
                      display: "flex",
                      margin: "2rem 0",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      PAYMENT VOUCHER
                    </div>
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      <NPInput
                        type={"text"}
                        prefixItem={`PT-${isFTLData.FY}-`}
                        onChange={(e) => {
                          setIsFTLData((prevData) => ({
                            ...prevData,
                            payment_voucher: e.target.value,
                          }));
                        }}
                        value={isFTLData.payment_voucher}
                      />
                    </div>
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      RECIEPTS VOUCHER
                    </div>
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      <NPInput
                        type={"text"}
                        prefixItem={`RT-${isFTLData.FY}-`}
                        onChange={(e) => {
                          setIsFTLData((prevData) => ({
                            ...prevData,
                            reciept_voucher: e.target.value,
                          }));
                        }}
                        value={isFTLData.reciept_voucher}
                      />
                    </div>
                  </div>

                  <div
                    className="pin-ver-1st"
                    style={{
                      display: "flex",
                      margin: "2rem 0",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      DELIVERY CHALLAN
                    </div>
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      <NPInput
                        prefixItem={`DC-${isFTLData.FY}-`}
                        onChange={(e) => {
                          setIsFTLData((prevData) => ({
                            ...prevData,
                            delivery_challan: e.target.value,
                          }));
                        }}
                        value={isFTLData.delivery_challan}
                        type={"text"}
                      />
                    </div>
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      QUOTATION
                    </div>
                    <div style={{ width: "calc(150px - .5rem)", padding: "0 .25rem" }}>
                      <NPInput
                        prefixItem={`QT-${isFTLData.FY}-`}
                        onChange={(e) => {
                          setIsFTLData((prevData) => ({
                            ...prevData,
                            quotation: e.target.value,
                          }));
                        }}
                        value={isFTLData.quotation}
                        type={"text"}
                      />
                    </div>
                  </div>

                  <div
                    className="pin-ver-1st"
                    style={{
                      display: "flex",
                      margin: "2rem 0",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ width: "150px", padding: "0 .25rem" }}>
                      PURCHASE ORDER
                    </div>
                    <div style={{ width: "150px", padding: "0 .25rem" }}>
                      <NPInput
                        prefixItem={`PO-${isFTLData.FY}-`}
                        onChange={(e) => {
                          setIsFTLData((prevData) => ({
                            ...prevData,
                            purchase_order: e.target.value,
                          }));
                        }}
                        value={isFTLData.purchase_order}
                        type={"text"}
                      />
                    </div>
                    <div style={{ width: "150px", padding: "0 .25rem" }}>
                      EXPENSES VOUCHER
                    </div>
                    <div style={{ width: "150px", padding: "0 .25rem" }}>
                      <NPInput
                        prefixItem={`EX-${isFTLData.FY}-`}
                        onChange={(e) => {
                          setIsFTLData((prevData) => ({
                            ...prevData,
                            expenses_voucher: e.target.value,
                          }));
                        }}
                        value={isFTLData.expenses_voucher}
                        type={"text"}
                      />
                    </div>
                  </div>

                  {isFTLMode && (
                    <>
                      <div
                        className="pin-ver-1st"
                        style={{
                          display: "flex",
                          margin: "2rem 0",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div style={{ width: "150px", padding: "0 .25rem" }}>
                          CASH BALANCE
                        </div>
                        <div style={{ width: "150px", padding: "0 .25rem" }}>
                          <Input
                            placeholder={"CASH BALANCE"}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              if (
                                (inputValue?.length < 12 &&
                                  /^(0|[1-9]\d{0,7})(\.\d{0,2})?$/.test(inputValue)) ||
                                e.target.value == 0
                              ) {
                                setIsFTLData((prevData) => ({
                                  ...prevData,
                                  cash_balance: /^(0|[1-9]\d{0,7})(\.\d{0,2})?$/.test(inputValue)
                                    ? inputValue
                                    : "",
                                }));
                              }
                            }}
                            value={isFTLData.cash_balance}
                            type={"text"}
                          />
                        </div>
                        <div style={{ width: "150px", padding: "0 .25rem" }}>
                          LOAD ACC OPEN BALANCE
                        </div>
                        <div style={{ width: "150px", padding: "0 .25rem" }}>
                          <Input
                            placeholder={"LOAD ACC BALANCE"}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              if (
                                (inputValue?.length < 12 &&
                                  /^(0|[1-9]\d{0,7})(\.\d{0,2})?$/.test(inputValue)) ||
                                e.target.value == 0
                              ) {
                                setIsFTLData((prevData) => ({
                                  ...prevData,
                                  load_balance: /^(0|[1-9]\d{0,7})(\.\d{0,2})?$/.test(inputValue)
                                    ? inputValue
                                    : "",
                                }));
                              }
                            }}
                            value={isFTLData.load_balance}
                            type={"text"}
                          />
                        </div>
                      </div>

                      <div
                        className="pin-ver-1st"
                        style={{
                          display: "flex",
                          margin: "2rem 0",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div style={{ width: "150px", padding: "0 .25rem" }}>
                          SVTV BALANCE
                        </div>
                        <div style={{ width: "150px", padding: "0 .25rem" }}>
                          <Input
                            placeholder={"SVTV BALANCE"}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              if (
                                (inputValue?.length < 12 &&
                                  /^(0|[1-9]\d{0,7})(\.\d{0,2})?$/.test(inputValue)) ||
                                e.target.value == 0
                              ) {
                                setIsFTLData((prevData) => ({
                                  ...prevData,
                                  svtv_balance: /^(0|[1-9]\d{0,7})(\.\d{0,2})?$/.test(inputValue)
                                    ? inputValue
                                    : "",
                                }));
                              }
                            }}
                            value={isFTLData.svtv_balance}
                            type={"text"}
                          />
                        </div>
                        <div style={{ width: "150px", padding: "0 .25rem" }}></div>
                        <div style={{ width: "150px", padding: "0 .25rem" }}></div>
                      </div>
                    </>
                  )}

                  <div
                    className="pin-ver-1st"
                    style={{
                      display: "flex",
                      margin: "2rem 0",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div style={{ width: "30%", padding: "0 5% " }}>
                      <Button
                        onClick={() => {
                          if (isFTLMode) {
                            HandleSubmitFTL();
                          } else {
                            handleSubmitData();
                          }
                        }}
                        title={"NEXT"}
                      />
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <h3 style={{ marginBottom: "1rem", fontSize: "16px", fontWeight: "600" }}>Godown 1 (Required)</h3>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", justifyContent: "center" }}>
                    <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                      <Input
                        label="GODOWN NAME"
                        placeholder={"GODOWN NAME"}
                        value={addData?.unit_name}
                        onChange={(e) => {
                          setAddData({ ...addData, unit_name: e.target.value });
                        }}
                      />
                    </div>
                    <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                      <Input
                        label="ADDRESS LINE 1"
                        placeholder={"ADDRESS"}
                        value={addData?.address1}
                        onChange={(e) => {
                          setAddData({ ...addData, address1: e.target.value });
                        }}
                      />
                    </div>
                    <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                      <Input
                        label="ADDRESS LINE 2"
                        placeholder={"ADDRESS"}
                        value={addData?.address2}
                        onChange={(e) => {
                          setAddData({ ...addData, address2: e.target.value });
                        }}
                      />
                    </div>
                    <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                      <Dropdown
                        label="STATE"
                        value={addData?.state}
                        options={
                          <>
                            <option>
                              {addData?.state?.length === 0 && stateList.length > 0
                                ? "SELECT"
                                : stateList.filter((v) => v.code === addData?.state)[0]?.name || "SELECT"}
                            </option>
                            {stateList?.length > 0 &&
                              stateList?.map((v, i) => (
                                <option key={i} value={v.code}>
                                  {v.name}
                                </option>
                              ))}
                          </>
                        }
                        onChange={(e) => {
                          setAddData({
                            ...addData,
                            state: e.target.value,
                            district: "",
                            location: "",
                            pincode: "",
                          });
                        }}
                        onBlur={() => handleDistrict3(addData?.state)}
                      />
                    </div>
                    <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                      <Dropdown
                        label="DISTRICT"
                        onChange={(e) =>
                          setAddData({
                            ...addData,
                            district: e.target.value,
                            location: "",
                            pincode: "",
                          })
                        }
                        value={addData?.district}
                        options={
                          <>
                            <option>
                              {addData?.district?.length === 0 || !addData?.district
                                ? "SELECT"
                                : addData?.district}
                            </option>
                            {Array.isArray(districtList2) &&
                              districtList2?.length > 0 &&
                              districtList2?.map((item, i) => (
                                <option key={i} value={item.name.toUpperCase()}>
                                  {item.name}
                                </option>
                              ))}
                          </>
                        }
                      />
                    </div>
                    <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                      <Input
                        label="PINCODE"
                        placeholder={"PINCODE"}
                        value={addData?.pincode}
                        onChange={(e) => {
                          if (e.target.value.length <= 6) {
                            let inputData = e.target.value;
                            const validInput = inputData.replace(/[^0-9]/g, "");
                            setAddData({
                              ...addData,
                              pincode: validInput,
                              location: "",
                            });
                          }
                          if (e.target.value.length == 6 && addData?.state?.length > 0) {
                            get_location(e.target.value, addData?.state);
                          }
                        }}
                      />
                    </div>
                    <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                      <Dropdown
                        label="LOCATION"
                        value={addData?.location}
                        options={
                          <>
                            <option>
                              {addData?.location?.length === 0 && locationList?.length > 0
                                ? "SELECT"
                                : addData?.location || "SELECT"}
                            </option>
                            {locationList?.length > 0 &&
                              locationList?.map((v, i) => (
                                <option key={i} value={v}>
                                  {v}
                                </option>
                              ))}
                          </>
                        }
                        onChange={(e) => {
                          setAddData({ ...addData, location: e.target.value });
                        }}
                      />
                    </div>
                  </div>

                  {!showSecondGodown && (
                    <div style={{ display: "flex", justifyContent: "center", margin: "1.5rem 0" }}>
                      <Button
                        onClick={() => setShowSecondGodown(true)}
                        title={"+ ADD SECOND GODOWN (OPTIONAL)"}
                        style={{ backgroundColor: "#28a745", padding: "0.5rem 1rem" }}
                      />
                    </div>
                  )}

                  {showSecondGodown && (
                    <>
                      <hr style={{ margin: "2rem 0", border: "1px solid #ddd" }} />
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                        <h3 style={{ fontSize: "16px", fontWeight: "600" }}>Godown 2 (Optional)</h3>
                        <button
                          onClick={() => {
                            setShowSecondGodown(false);
                            setAddData2({
                              unit_name: "",
                              address1: "",
                              address2: "",
                              state: "",
                              district: "",
                              pincode: "",
                              location: "",
                            });
                          }}
                          style={{
                            background: "#dc3545",
                            color: "white",
                            border: "none",
                            padding: "0.5rem 1rem",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        >
                          ✕ Remove
                        </button>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", justifyContent: "center" }}>
                        <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                          <Input
                            label="GODOWN NAME"
                            placeholder={"GODOWN NAME"}
                            value={addData2?.unit_name}
                            onChange={(e) => {
                              setAddData2({ ...addData2, unit_name: e.target.value });
                            }}
                          />
                        </div>
                        <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                          <Input
                            label="ADDRESS LINE 1"
                            placeholder={"ADDRESS"}
                            value={addData2?.address1}
                            onChange={(e) => {
                              setAddData2({ ...addData2, address1: e.target.value });
                            }}
                          />
                        </div>
                        <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                          <Input
                            label="ADDRESS LINE 2"
                            placeholder={"ADDRESS"}
                            value={addData2?.address2}
                            onChange={(e) => {
                              setAddData2({ ...addData2, address2: e.target.value });
                            }}
                          />
                        </div>
                        <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                          <Dropdown
                            label="STATE"
                            value={addData2?.state}
                            options={
                              <>
                                <option>
                                  {addData2?.state?.length === 0 && stateList.length > 0
                                    ? "SELECT"
                                    : stateList.filter((v) => v.code === addData2?.state)[0]?.name || "SELECT"}
                                </option>
                                {stateList?.length > 0 &&
                                  stateList?.map((v, i) => (
                                    <option key={i} value={v.code}>
                                      {v.name}
                                    </option>
                                  ))}
                              </>
                            }
                            onChange={(e) => {
                              setAddData2({
                                ...addData2,
                                state: e.target.value,
                                district: "",
                                location: "",
                                pincode: "",
                              });
                            }}
                            onBlur={() => handleDistrict4(addData2?.state)}
                          />
                        </div>
                        <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                          <Dropdown
                            label="DISTRICT"
                            onChange={(e) =>
                              setAddData2({
                                ...addData2,
                                district: e.target.value,
                                location: "",
                                pincode: "",
                              })
                            }
                            value={addData2?.district}
                            options={
                              <>
                                <option>
                                  {addData2?.district?.length === 0 || !addData2?.district
                                    ? "SELECT"
                                    : addData2?.district}
                                </option>
                                {Array.isArray(districtList3) &&
                                  districtList3?.length > 0 &&
                                  districtList3?.map((item, i) => (
                                    <option key={i} value={item.name.toUpperCase()}>
                                      {item.name}
                                    </option>
                                  ))}
                              </>
                            }
                          />
                        </div>
                        <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                          <Input
                            label="PINCODE"
                            placeholder={"PINCODE"}
                            value={addData2?.pincode}
                            onChange={(e) => {
                              if (e.target.value.length <= 6) {
                                let inputData = e.target.value;
                                const validInput = inputData.replace(/[^0-9]/g, "");
                                setAddData2({
                                  ...addData2,
                                  pincode: validInput,
                                  location: "",
                                });
                              }
                              if (e.target.value.length == 6 && addData2?.state?.length > 0) {
                                get_location2(e.target.value, addData2?.state);
                              }
                            }}
                          />
                        </div>
                        <div style={{ width: "240px", padding: ".15rem .5rem" }}>
                          <Dropdown
                            label="LOCATION"
                            value={addData2?.location}
                            options={
                              <>
                                <option>
                                  {addData2?.location?.length === 0 && locationList2?.length > 0
                                    ? "SELECT"
                                    : addData2?.location || "SELECT"}
                                </option>
                                {locationList2?.length > 0 &&
                                  locationList2?.map((v, i) => (
                                    <option key={i} value={v}>
                                      {v}
                                    </option>
                                  ))}
                              </>
                            }
                            onChange={(e) => {
                              setAddData2({ ...addData2, location: e.target.value });
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div
                    className="pin-ver-1st"
                    style={{
                      display: "flex",
                      margin: "2rem 0",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div style={{ width: "30%", padding: "0 5% " }}>
                      <Button onClick={handleAddAddress} title={"NEXT"} />
                    </div>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <div className="pin-ver">
                    <div
                      className="pin-ver-1st"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "2rem",
                      }}
                    >
                      <div
                        style={{
                          width: "70%",
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                        }}
                      >
                        <label style={{ width: "30%", fontSize: "14px" }}>
                          Pin Number:
                        </label>
                        <div style={{ width: "70%" }}>
                          <Input
                            type={"password"}
                            placeholder={"Enter Pin Here"}
                            value={pinData?.set_pin}
                            onChange={(event) => {
                              const numericValue = event.target.value.replace(/[^0-9]/g, "");
                              if (numericValue.length <= 4) {
                                setPinData({ ...pinData, set_pin: numericValue });
                              }
                            }}
                          />
                          <Input
                            type={"password"}
                            placeholder={"Enter Confirm Pin Here"}
                            value={pinData?.confirm_pin}
                            onChange={(event) => {
                              const numericValue = event.target.value.replace(/[^0-9]/g, "");
                              if (numericValue.length <= 4) {
                                setPinData({ ...pinData, confirm_pin: numericValue });
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ width: "30%", padding: "0 5% " }}>
                        <Button onClick={handle_set_pin} title={"SUBMIT"} />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          }
        />
      )}

      {open1 && (
        <SmallModal
          title={"Alerts"}
          isOpen={open1}
          setIsOpen={setOpen1}
          mess={mess}
          okbtn={true}
        />
      )}

      {showLoader && <Loader loading={showLoader} />}
    </div>
  );
};
